import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'wouter';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { apiRequest } from '../../lib/queryClient';

// Fix default marker icons in React/Webpack
const icon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function FitBounds({ positions }: { positions: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length === 0) return;
    if (positions.length === 1) {
      map.setView(positions[0], 12);
    } else {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [20, 20], maxZoom: 14 });
    }
  }, [map, positions]);
  return null;
}

type GeocodeInput = { address: string } | { street?: string; city?: string; state?: string; postalcode?: string };

async function geocodeWithApi(input: GeocodeInput): Promise<[number, number] | null> {
  try {
    const params = 'address' in input
      ? `address=${encodeURIComponent(input.address)}`
      : ['street', 'city', 'state', 'postalcode']
          .filter((k) => (input as any)[k])
          .map((k) => `${k}=${encodeURIComponent((input as any)[k])}`)
          .join('&');
    const res = await apiRequest('GET', `/api/protected/geocode?${params}`);
    const data = await res.json();
    if (data && typeof data.lat === 'number' && typeof data.lon === 'number') {
      return [data.lat, data.lon];
    }
  } catch (e) {
    throw e; // Fall through to Nominatim
  }
  return null;
}

async function geocodeWithNominatim(input: GeocodeInput): Promise<[number, number] | null> {
  try {
    let url: string;
    if ('address' in input) {
      const q = input.address.includes('USA') ? input.address : `${input.address}, USA`;
      url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1`;
    } else {
      const params = new URLSearchParams({ format: 'json', limit: '1', country: 'USA' });
      if (input.street) params.set('street', input.street);
      if (input.city) params.set('city', input.city);
      if (input.state) params.set('state', input.state);
      if (input.postalcode) params.set('postalcode', input.postalcode);
      url = `https://nominatim.openstreetmap.org/search?${params.toString()}`;
    }
    const res = await fetch(url,
      { headers: { 'Accept-Language': 'en', 'User-Agent': 'Remodra-Dashboard/1.0 (https://getremodra.com)' } }
    );
    const data = await res.json();
    if (data && data[0]) {
      return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
    }
  } catch (e) {
    console.warn('Nominatim geocoding failed for:', address, e);
  }
  return null;
}

async function geocodeAddress(input: GeocodeInput): Promise<[number, number] | null> {
  const hasAddress = 'address' in input && input.address;
  const hasStructured = !('address' in input) && (input.street || input.city || input.state);
  if (!hasAddress && !hasStructured) return null;
  if (hasAddress && (input.address === 'Unknown' || !input.address.trim())) return null;
  // Try server API first; fallback to Nominatim if API fails
  try {
    const coords = await geocodeWithApi(input);
    if (coords) return coords;
  } catch {
    // API unavailable - try Nominatim directly
  }
  return geocodeWithNominatim(input);
}

export default function ProjectsMap({
  projects,
  clients,
}: {
  projects: any[];
  clients: any[];
}) {
  const [markers, setMarkers] = useState<{ position: [number, number]; project: any; label: string }[]>([]);
  const geocodeCache = useRef<Record<string, [number, number] | null>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      const results: { position: [number, number]; project: any; label: string }[] = [];

      for (const p of projects) {
        const pid = p.clientId ?? p.client_id;
        const client = p.client ?? clients.find((c: any) => String(c.id) === String(pid));
        const addr = client?.address ?? client?.street_address ?? '';
        const city = client?.city ?? '';
        const state = client?.state ?? '';
        const zip = client?.zip ?? '';
        const parts = [addr, city, state, zip].filter(Boolean);
        const addressStr = parts.length > 0 ? parts.join(', ') : (city && state ? `${city}, ${state}` : city || state || null);
        const cacheKey = addressStr || 'unknown';
        if (!addressStr || addressStr === 'Unknown') continue;

        let coords = geocodeCache.current[cacheKey];
        if (coords === undefined) {
          // Use structured geocoding (street, city, state) for better accuracy
          const structured = addr || city || state;
          const input: GeocodeInput = structured
            ? { street: addr || undefined, city: city || undefined, state: state || undefined, postalcode: zip || undefined }
            : { address: addressStr };
          coords = await geocodeAddress(input);
          // Fallback: try full address string if structured fails
          if (!coords && structured && addressStr) {
            coords = await geocodeAddress({ address: addressStr });
          }
          if (!coords && (city || state)) {
            const fallback = [city, state, zip].filter(Boolean).join(', ');
            if (fallback && fallback !== addressStr) {
              coords = await geocodeAddress({ address: fallback });
            }
          }
          geocodeCache.current[cacheKey] = coords;
          await new Promise((r) => setTimeout(r, 1100)); // Nominatim rate limit
        }
        if (cancelled) return;
        if (coords) {
          results.push({
            position: coords,
            project: p,
            label: p.title || addressStr,
          });
        }
      }

      if (!cancelled) {
        setMarkers(results);
      }
      setLoading(false);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [projects, clients]);

  const positions = useMemo(() => markers.map((m) => m.position), [markers]);
  // Default view: neutral world center. Map is never based on user location—only project markers.
  const defaultCenter: [number, number] = [20, 0];
  const defaultZoom = 2;

  if (loading && markers.length === 0) {
    return (
      <div className="h-[320px] flex items-center justify-center bg-[var(--dash-bg)] rounded-lg border border-[var(--dash-border)]">
        <p className="text-[var(--dash-text-muted)]">Loading project locations...</p>
      </div>
    );
  }

  // Always show the map so users see the tiles/zoom; overlay message when no pins
  return (
    <div className="h-[320px] rounded-lg overflow-hidden border border-[var(--dash-border)] relative">
      {!loading && markers.length === 0 && (
        <div className="absolute inset-0 z-[1000] flex flex-col items-center justify-center bg-[var(--dash-bg)]/90 gap-2 px-4 rounded-lg">
          <p className="text-[var(--dash-text-muted)] text-center font-medium">No locations found for projects.</p>
          <p className="text-[var(--dash-text-muted)] text-center text-sm">Add Address, City, State, and Zip for each client to show them on the map.</p>
          <Link href="/clients" className="text-sm text-[var(--dash-accent)] hover:underline mt-1">Go to Clients →</Link>
        </div>
      )}
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {markers.map((m, i) => (
          <Marker key={i} position={m.position} icon={icon}>
            <Popup>
              <strong>{m.project.title || 'Project'}</strong>
              <br />
              {m.label}
            </Popup>
          </Marker>
        ))}
        {positions.length > 0 && <FitBounds positions={positions} />}
      </MapContainer>
    </div>
  );
}
